// MyAccount.tsx — #246 simplified hub: identity + destination map; Edit settings = modal
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import {
    Calendar,
    ChevronDown,
    ChevronRight,
    Heart,
    Package,
    Star,
    Truck,
    Users,
    type LucideIcon,
} from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';
import BackToHomeLink from '../components/BackToHomeLink';
import AccountProfileCard from '../components/account/AccountProfileCard';
import { useTranslation } from '../context/TranslationContext';
import { translateWithFallback } from '../utils/translateWithFallback';
import { useFrontendAuth } from '../hooks/useAuth';
import { useProtectedQuery } from '../hooks/useProtectedQuery';
import {
    getMyProducts,
    getUserProfile,
    getFavourites,
    getSellerSummary,
    getSellerReviews,
    getSellerBreakdown,
    getMyFollowing,
    getMyOrders,
    getMySales,
    unfollowUser,
} from '../services/api.shop';
import { FollowingUserDto, Order, Product, ProductStatus, UserProfileDto } from '../types';
import ReviewSummaryCard from '../components/profile/ReviewSummaryCard';
import ReviewListItem from '../components/profile/ReviewListItem';
import ProfileSettingsPanel from '../components/profile/ProfileSettingsPanel';
import { invalidateSellerListingCaches } from '../utils/invalidateSellerListingCaches';
import { publicProfileHeading } from '../utils/usernameRules';

/** Settings opens Edit settings modal; reviews/following expand under Community. */
type HubMode = 'home' | 'settings';
type CommunityPanel = 'reviews' | 'following' | null;

const parseMode = (raw: string | null): HubMode =>
    raw === 'settings' ? 'settings' : 'home';

const parseCommunity = (raw: string | null): CommunityPanel => {
    if (raw === 'reviews' || raw === 'following') return raw;
    return null;
};

function isActiveListing(p: Product) {
    return !p.isSold && p.status === ProductStatus.Active;
}

const AccountActivityCard = ({
    to,
    icon: Icon,
    title,
    subtitle,
}: {
    to: string;
    icon: LucideIcon;
    title: string;
    subtitle: string;
}) => (
    <Link
        to={to}
        className="flex min-h-[4.5rem] items-center gap-3 rounded-2xl border border-[color-mix(in_srgb,var(--accent-color),transparent_40%)] bg-[color-mix(in_srgb,var(--bg-color),white_25%)] px-4 py-3.5 transition hover:bg-[color-mix(in_srgb,var(--accent-color),transparent_88%)]"
    >
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[var(--accent-color)]/20 text-[var(--text-color)]">
            <Icon className="h-5 w-5" aria-hidden />
        </span>
        <span className="min-w-0 flex-1 text-left">
            <span className="block text-sm font-semibold leading-snug text-[var(--text-color)]">
                {title}
            </span>
            <span className="mt-0.5 block text-xs leading-snug text-[color-mix(in_srgb,var(--text-color),transparent_40%)]">
                {subtitle}
            </span>
        </span>
        <ChevronRight
            className="h-4 w-4 shrink-0 text-[color-mix(in_srgb,var(--text-color),transparent_50%)]"
            aria-hidden
        />
    </Link>
);

/** Expand/collapse control — stays vertically centered on hover; chevron rotates when open. */
const CommunityToggleCard = ({
    icon: Icon,
    title,
    subtitle,
    expanded,
    onClick,
}: {
    icon: LucideIcon;
    title: string;
    subtitle: string;
    expanded: boolean;
    onClick: () => void;
}) => (
    <button
        type="button"
        onClick={onClick}
        aria-expanded={expanded}
        className={`flex min-h-[4.5rem] w-full items-center gap-3 rounded-2xl border px-4 py-3.5 text-left transition ${
            expanded
                ? 'border-[var(--accent-color)] bg-[color-mix(in_srgb,var(--accent-color),transparent_88%)] shadow-sm'
                : 'border-[color-mix(in_srgb,var(--accent-color),transparent_40%)] bg-[color-mix(in_srgb,var(--bg-color),white_25%)] hover:bg-[color-mix(in_srgb,var(--accent-color),transparent_88%)]'
        }`}
    >
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[var(--accent-color)]/20 text-[var(--text-color)]">
            <Icon className="h-5 w-5" aria-hidden />
        </span>
        <span className="min-w-0 flex-1">
            <span className="block text-sm font-semibold leading-snug text-[var(--text-color)]">
                {title}
            </span>
            <span className="mt-0.5 block text-xs leading-snug text-[color-mix(in_srgb,var(--text-color),transparent_40%)]">
                {subtitle}
            </span>
        </span>
        <ChevronDown
            className={`h-4 w-4 shrink-0 text-[color-mix(in_srgb,var(--text-color),transparent_50%)] transition-transform duration-200 ${
                expanded ? 'rotate-180' : ''
            }`}
            aria-hidden
        />
    </button>
);

const MyAccount = () => {
    const { t } = useTranslation();
    const tr = (key: string, fb: string) => translateWithFallback(t, key, fb);
    const { isAuthenticated, isAuthReady, user } = useFrontendAuth();
    const queryClient = useQueryClient();
    const [searchParams, setSearchParams] = useSearchParams();
    const tabParam = searchParams.get('tab') ?? searchParams.get('view');
    const [mode, setMode] = useState<HubMode>(() => parseMode(tabParam));
    const [communityPanel, setCommunityPanel] = useState<CommunityPanel>(() =>
        parseCommunity(tabParam)
    );
    const communityPanelRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const raw = searchParams.get('tab') ?? searchParams.get('view');
        setMode(parseMode(raw));
        // Only open community from URL when not in settings
        if (parseMode(raw) === 'home') {
            setCommunityPanel(parseCommunity(raw));
        }
    }, [searchParams]);

    useEffect(() => {
        void invalidateSellerListingCaches(queryClient, user?.id);
        // Following list must not stick on a pre-follow cache (refetchOnMount is false by default)
        void queryClient.invalidateQueries({ queryKey: ['myFollowing'] });
    }, [queryClient, user?.id]);

    const openSettings = () => {
        setMode('settings');
        setCommunityPanel(null);
        setSearchParams({ tab: 'settings' }, { replace: true });
    };

    const closeSettings = () => {
        setMode('home');
        setSearchParams({}, { replace: true });
    };

    /** One open at a time; re-click active panel closes. */
    const toggleCommunity = (panel: 'reviews' | 'following') => {
        setMode('home');
        const next: CommunityPanel = communityPanel === panel ? null : panel;
        setCommunityPanel(next);
        if (next) {
            setSearchParams({ tab: next }, { replace: true });
        } else {
            setSearchParams({}, { replace: true });
        }
    };

    /**
     * Scroll the opened panel to just below the sticky shop header.
     * `scrollIntoView({ block: 'nearest' })` often no-ops or jumps the wrong way on mobile
     * when the toggle is already visible — use absolute Y instead.
     */
    useEffect(() => {
        if (!communityPanel) return;

        let cancelled = false;
        const scrollToPanel = () => {
            if (cancelled) return;
            const el = communityPanelRef.current;
            if (!el) return;

            const rawHeader =
                getComputedStyle(document.documentElement)
                    .getPropertyValue('--shop-header-height')
                    .trim() || '7rem';
            // rem or px
            let headerPx = 112;
            if (rawHeader.endsWith('rem')) {
                const rem = parseFloat(rawHeader) || 7;
                headerPx = rem * (parseFloat(getComputedStyle(document.documentElement).fontSize) || 16);
            } else {
                headerPx = parseFloat(rawHeader) || 112;
            }
            const gap = 12;
            const y = el.getBoundingClientRect().top + window.scrollY - headerPx - gap;
            window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' });
        };

        // Wait until the panel is in the DOM and laid out (mobile needs more than one frame)
        const t0 = window.setTimeout(scrollToPanel, 50);
        const t1 = window.setTimeout(scrollToPanel, 200);
        return () => {
            cancelled = true;
            window.clearTimeout(t0);
            window.clearTimeout(t1);
        };
    }, [communityPanel]);

    const {
        data: profile,
        isLoading: profileLoading,
        error: profileError,
    } = useProtectedQuery<UserProfileDto>({
        queryKey: ['userProfile'],
        queryFn: getUserProfile,
    });

    const { data: products = [] } = useProtectedQuery<Product[]>({
        queryKey: ['myProducts'],
        queryFn: getMyProducts,
        staleTime: 30 * 1000,
    });

    const { data: favourites = [] } = useProtectedQuery<Product[]>({
        queryKey: ['favourites'],
        queryFn: getFavourites,
    });

    const { data: sellerSummary } = useProtectedQuery({
        queryKey: ['sellerSummary', user?.id],
        queryFn: () => getSellerSummary(user!.id),
        enabled: !!user?.id,
    });

    const { data: breakdown } = useQuery({
        queryKey: ['sellerBreakdown', user?.id],
        queryFn: () => getSellerBreakdown(user!.id),
        enabled: !!user?.id && communityPanel === 'reviews',
    });

    const { data: reviews = [] } = useQuery({
        queryKey: ['sellerReviews', user?.id],
        queryFn: () => getSellerReviews(user!.id, 1, 50),
        enabled: !!user?.id && communityPanel === 'reviews',
    });

    const {
        data: myFollowing = [],
        isLoading: followingLoading,
    } = useProtectedQuery<FollowingUserDto[]>({
        queryKey: ['myFollowing'],
        queryFn: getMyFollowing,
        staleTime: 0,
        refetchOnMount: true,
    });

    const { data: myOrders = [] } = useProtectedQuery<Order[]>({
        queryKey: ['myOrders'],
        queryFn: getMyOrders,
        staleTime: 30 * 1000,
    });

    const { data: mySales = [] } = useProtectedQuery<Order[]>({
        queryKey: ['mySales'],
        queryFn: getMySales,
        staleTime: 30 * 1000,
    });

    const activeCount = useMemo(() => products.filter(isActiveListing).length, [products]);
    const salesToShip = useMemo(() => mySales.filter((o) => o.canShip).length, [mySales]);
    const purchasesInProgress = useMemo(() => {
        const done = new Set(['Delivered', 'Completed', 'Cancelled', 'Refunded']);
        return myOrders.filter((o) => !done.has(String(o.status ?? ''))).length;
    }, [myOrders]);

    if (!isAuthReady || !isAuthenticated) {
        return <LoadingSpinner />;
    }

    if (profileLoading) return <LoadingSpinner />;
    if (profileError || !profile) {
        return (
            <p className="rounded-lg bg-red-50 px-4 py-3 text-red-700">
                {tr('account.hub.loaderror', 'Unable to load your account. Please try again.')}
            </p>
        );
    }

    const firstName =
        profile.firstName?.trim() || profile.userName?.split(' ')[0] || profile.userName;
    const reviewCount = sellerSummary?.ratingCount || 0;
    const avgRating =
        sellerSummary && sellerSummary.ratingCount > 0
            ? sellerSummary.averageRating.toFixed(1)
            : null;

    return (
        <div className="mx-auto max-w-3xl py-4 sm:py-8">
            <BackToHomeLink />

            <h1 className="mb-6 text-2xl font-bold text-[var(--text-color)]">
                {tr('account.welcome', 'Welcome back, {name}!').replace('{name}', firstName)}
            </h1>

            <AccountProfileCard
                profile={profile}
                publicProfileUserId={user?.id}
                settingsActive={mode === 'settings'}
                onOpenSettings={() => (mode === 'settings' ? closeSettings() : openSettings())}
            />

            <ProfileSettingsPanel
                profile={profile}
                open={mode === 'settings'}
                onClose={closeSettings}
            />

            <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-[color-mix(in_srgb,var(--text-color),transparent_35%)]">
                {tr('account.hub.section.commerce', 'Buying & selling')}
            </h2>
            <div className="mb-4 grid grid-cols-1 gap-2 sm:grid-cols-2">
                <AccountActivityCard
                    to="/my-account/orders"
                    icon={Calendar}
                    title={tr('account.myorders', 'My purchases')}
                    subtitle={
                        myOrders.length === 0
                            ? tr('account.hub.purchases.empty', 'No purchases yet')
                            : purchasesInProgress > 0
                              ? tr(
                                    'account.hub.purchases.inprogress',
                                    '{count} in progress'
                                ).replace('{count}', String(purchasesInProgress))
                              : tr('account.hub.purchases.count', '{count} orders').replace(
                                    '{count}',
                                    String(myOrders.length)
                                )
                    }
                />
                <AccountActivityCard
                    to="/my-account/sales"
                    icon={Truck}
                    title={tr('account.sales', 'My sales')}
                    subtitle={
                        mySales.length === 0
                            ? tr(
                                  'account.hub.sales.empty',
                                  'When something sells, ship from here'
                              )
                            : salesToShip > 0
                              ? tr('account.hub.sales.toship', '{count} to ship').replace(
                                    '{count}',
                                    String(salesToShip)
                                )
                              : tr(
                                    'account.hub.sales.nonepending',
                                    'Nothing to ship right now'
                                )
                    }
                />
                <AccountActivityCard
                    to="/my-account/listings"
                    icon={Package}
                    title={tr('account.listings.title', 'My Listings')}
                    subtitle={
                        activeCount === 0
                            ? tr('account.hub.listings.empty', 'No listings yet')
                            : tr('account.hub.listings.summary', '{active} active').replace(
                                  '{active}',
                                  String(activeCount)
                              )
                    }
                />
                <AccountActivityCard
                    to="/favourites"
                    icon={Heart}
                    title={tr('account.stats.saved', 'Saved')}
                    subtitle={
                        favourites.length === 0
                            ? tr(
                                  'account.hub.saved.empty',
                                  'Items you heart along the way'
                              )
                            : tr('account.hub.saved.count', '{count} saved').replace(
                                  '{count}',
                                  String(favourites.length)
                              )
                    }
                />
            </div>

            <Link
                to="/sell"
                className="mb-8 flex min-h-14 items-center justify-center gap-2 rounded-2xl border border-dashed border-[color-mix(in_srgb,var(--accent-color),transparent_35%)] bg-[color-mix(in_srgb,var(--accent-color),transparent_92%)] px-4 py-3.5 text-sm font-semibold text-[var(--text-color)] shadow-sm transition hover:border-[var(--accent-color)] hover:bg-[color-mix(in_srgb,var(--accent-color),transparent_85%)]"
            >
                <Package className="h-4 w-4 opacity-80" aria-hidden />
                <span>{tr('profile.listings.listItem', 'List an item')}</span>
                <span className="hidden font-normal text-[color-mix(in_srgb,var(--text-color),transparent_40%)] sm:inline">
                    - {tr('profile.listings.listItem.sub', 'Something your baby has grown out of')}
                </span>
            </Link>

            <div>
                <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-[color-mix(in_srgb,var(--text-color),transparent_35%)]">
                    {tr('account.hub.section.community', 'Community')}
                </h2>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    <CommunityToggleCard
                        icon={Star}
                        title={tr('profile.tab.reviews', 'Reviews')}
                        subtitle={
                            avgRating
                                ? tr(
                                      'account.hub.reviews.summary',
                                      '{rating} · {count} reviews'
                                  )
                                      .replace('{rating}', avgRating)
                                      .replace('{count}', String(reviewCount))
                                : tr(
                                      'account.hub.reviews.hint',
                                      'What buyers say about you'
                                  )
                        }
                        expanded={communityPanel === 'reviews'}
                        onClick={() => toggleCommunity('reviews')}
                    />
                    <CommunityToggleCard
                        icon={Users}
                        title={tr(
                            'account.hub.following.title',
                            'Manage who you follow'
                        )}
                        subtitle={
                            myFollowing.length > 0
                                ? tr(
                                      'account.hub.following.count',
                                      '{count} people'
                                  ).replace('{count}', String(myFollowing.length))
                                : tr(
                                      'account.hub.following.empty',
                                      'Unfollow or open their profile'
                                  )
                        }
                        expanded={communityPanel === 'following'}
                        onClick={() => toggleCommunity('following')}
                    />
                </div>

                {communityPanel === 'reviews' && (
                    <div
                        ref={communityPanelRef}
                        id="account-community-reviews"
                        className="mt-3 space-y-4 rounded-2xl border border-[color-mix(in_srgb,var(--accent-color),transparent_50%)] bg-[color-mix(in_srgb,var(--bg-color),white_20%)] p-4 sm:p-5 scroll-mt-[calc(var(--shop-header-height,7rem)+0.75rem)]"
                    >
                        {breakdown && <ReviewSummaryCard breakdown={breakdown} />}
                        {reviews.length === 0 ? (
                            <p className="py-6 text-center text-sm text-[color-mix(in_srgb,var(--text-color),transparent_40%)]">
                                {tr('profile.reviews.empty', 'No reviews yet.')}
                            </p>
                        ) : (
                            reviews.map((r) => <ReviewListItem key={r.id} review={r} />)
                        )}
                    </div>
                )}

                {communityPanel === 'following' && (
                    <div
                        ref={communityPanelRef}
                        id="account-community-following"
                        className="mt-3 rounded-2xl border border-[color-mix(in_srgb,var(--accent-color),transparent_50%)] bg-[color-mix(in_srgb,var(--bg-color),white_20%)] p-4 sm:p-5 scroll-mt-[calc(var(--shop-header-height,7rem)+0.75rem)]"
                    >
                        <p className="mb-4 text-sm text-[color-mix(in_srgb,var(--text-color),transparent_40%)]">
                            {tr(
                                'profile.following.private.hint',
                                'Only you can see who you follow. Others only see your follower count. You can open a profile or unfollow.'
                            )}
                        </p>
                        {followingLoading ? (
                            <LoadingSpinner />
                        ) : myFollowing.length === 0 ? (
                            <p className="rounded-2xl border border-dashed border-[color-mix(in_srgb,var(--accent-color),transparent_45%)] px-6 py-10 text-center text-sm text-[color-mix(in_srgb,var(--text-color),transparent_40%)]">
                                {tr(
                                    'profile.following.empty',
                                    'You are not following anyone yet.'
                                )}
                            </p>
                        ) : (
                            <ul className="divide-y divide-[color-mix(in_srgb,var(--accent-color),transparent_70%)] overflow-hidden rounded-2xl border border-[color-mix(in_srgb,var(--accent-color),transparent_60%)] bg-[color-mix(in_srgb,var(--bg-color),white_15%)]">
                                {myFollowing.map((person) => (
                                    <AccountFollowingRow
                                        key={person.id}
                                        person={person}
                                        onUnfollowed={() => {
                                            void queryClient.invalidateQueries({
                                                queryKey: ['myFollowing'],
                                            });
                                            void queryClient.invalidateQueries({
                                                queryKey: ['publicProfile', person.id],
                                            });
                                            void queryClient.invalidateQueries({
                                                queryKey: ['followFeed'],
                                            });
                                            void queryClient.invalidateQueries({
                                                queryKey: ['followFeedBadge'],
                                            });
                                        }}
                                    />
                                ))}
                            </ul>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

const AccountFollowingRow = ({
    person,
    onUnfollowed,
}: {
    person: FollowingUserDto;
    onUnfollowed?: () => void;
}) => {
    const { t } = useTranslation();
    const tr = (key: string, fb: string) => translateWithFallback(t, key, fb);
    const [error, setError] = useState<string | null>(null);

    const mutation = useMutation({
        mutationFn: () => unfollowUser(person.id),
        onSuccess: () => {
            setError(null);
            onUnfollowed?.();
        },
        onError: () => setError(tr('profile.following.unfollow.failed', 'Could not unfollow.')),
    });

    const heading = publicProfileHeading({
        userName: person.userName,
        displayName: person.displayName,
        showPersonLine: person.showPersonLine,
    });
    const initials = (heading.title || '?').replace(/^@/, '').charAt(0).toUpperCase();

    return (
        <li className="flex flex-wrap items-center gap-3 px-4 py-3 sm:flex-nowrap">
            <Link to={`/users/${person.id}`} className="flex min-w-0 flex-1 items-center gap-3">
                {person.profilePictureUrl ? (
                    <img
                        src={person.profilePictureUrl}
                        alt=""
                        className="h-12 w-12 shrink-0 rounded-full object-cover"
                    />
                ) : (
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[var(--accent-color)] text-lg font-bold text-white">
                        {initials}
                    </div>
                )}
                <div className="min-w-0">
                    <p className="truncate font-medium text-[var(--text-color)]">{heading.title}</p>
                    {heading.subtitle && (
                        <p className="truncate text-xs text-[var(--text-color)]">{heading.subtitle}</p>
                    )}
                    <p className="text-xs text-[color-mix(in_srgb,var(--text-color),transparent_45%)]">
                        {person.activeListingsCount}{' '}
                        {tr('profile.stats.listings', 'Active listings').toLowerCase()}
                    </p>
                </div>
            </Link>
            <button
                type="button"
                disabled={mutation.isPending}
                onClick={() => mutation.mutate()}
                className="min-h-11 shrink-0 rounded-full border border-[color-mix(in_srgb,var(--accent-color),transparent_40%)] bg-[var(--bg-color)] px-4 py-2 text-sm font-medium text-[var(--text-color)] shadow-sm hover:bg-[color-mix(in_srgb,var(--accent-color),transparent_85%)] disabled:opacity-50"
            >
                {mutation.isPending
                    ? tr('state.loading', 'Loading…')
                    : tr('follow.button.unfollow', 'Unfollow')}
            </button>
            {error && <p className="w-full text-xs text-red-600 sm:basis-full">{error}</p>}
        </li>
    );
};

export default MyAccount;
